import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { getEmailPreferences, updateEmailPreferences, } from "@/modules/consent/infrastructure/adapters/emailPreferencesService";
import { CATCH_ALL_PURPOSE, } from "@/modules/consent/domain/types";
import { logger } from "@/services/loggerService";
import { setLocale, getSessionSelectedLocale } from "@/shared/i18n";
export function useEmailPreferences(token) {
    const state = ref("loading");
    const data = ref(null);
    const purposes = ref([]);
    const { t, te, locale } = useI18n();
    const router = useRouter();
    /** True while a save / unsubscribe request is in flight — locks the checkboxes and both buttons. */
    const isBusy = computed(() => state.value === "saving" || state.value === "unsubscribing");
    /**
     * Display name for the "you are currently subscribed to <X>" line: the tenant catalogue label
     * resolved from the token's purpose, else the raw purpose code. Undefined when the token carried
     * no purpose — the whole line is then hidden (no generic placeholder).
     */
    const subscribedNewsletter = computed(() => data.value?.subscribedPurposeLabel || data.value?.subscribedPurpose || undefined);
    /** Purposes grouped by their communication channel, rendered under one category header each. */
    const groups = computed(() => {
        const byChannel = new Map();
        for (const row of purposes.value) {
            const channel = row.channel || "Email";
            const bucket = byChannel.get(channel);
            if (bucket) {
                bucket.push(row);
            }
            else {
                byChannel.set(channel, [row]);
            }
        }
        return [...byChannel.entries()].map(([channel, items]) => ({ channel, items }));
    });
    /**
     * Purpose title: prefer the tenant-authored catalogue label from the API; fall back to the FE i18n
     * label, then the raw code. The API value is tenant- and locale-specific, so it wins over the
     * generic Tolgee label (which only covers the standard seeded codes).
     */
    function purposeLabel(row) {
        if (row.label) {
            return row.label;
        }
        const key = `emailPreferences.purpose.${row.purpose}`;
        return te(key) ? t(key) : row.purpose;
    }
    /** Optional purpose sub-title — the catalogue Description for the active locale (may be absent). */
    function purposeDescription(row) {
        return row.description ?? undefined;
    }
    /**
     * Toggle a purpose row from a click anywhere on its card. The OPAL Checkbox stops propagation on
     * its own clicks, so this only fires for clicks on the rest of the card. No-op while a request is in flight.
     */
    function toggleRow(row) {
        if (!isBusy.value) {
            row.checked = !row.checked;
        }
    }
    function isNotFound(e) {
        return e?.response?.status === 404;
    }
    /** Maps a load/save failure onto the matching terminal state (expired link vs generic error). */
    function handleFailure(e, action) {
        if (isNotFound(e)) {
            state.value = "expired";
        }
        else {
            state.value = "error";
            logger.error(`Failed to ${action} email preferences`, { error: String(e) });
        }
    }
    /**
     * The locale this view last applied from the API's resolvedLocale. Lets the `locale` watcher tell a
     * user-initiated switch (refetch) apart from our own setLocale on load (ignore) — no redundant fetch.
     */
    let appliedLocale = null;
    /**
     * Fetch the recipient's purposes/statuses and apply the backend-resolved page language.
     * @param selectedLocale set on a manual in-page switch — it outranks the contact snapshot server-side.
     */
    async function load(selectedLocale) {
        state.value = "loading";
        // On mount (no explicit arg) replay the user's in-session switcher choice so a deliberate language
        // pick outranks the contact snapshot when the view remounts (e.g. navigating back from the
        // confirmation screen). FE-session only — the choice is never persisted server-side.
        const effectiveLocale = selectedLocale ?? getSessionSelectedLocale() ?? undefined;
        try {
            const result = await getEmailPreferences(token, {
                selectedLocale: effectiveLocale,
                fallbackLocale: locale.value,
            });
            data.value = result;
            purposes.value = result.purposes
                .map((p) => ({
                channel: p.channel,
                purpose: p.purpose,
                checked: p.status === "OptedIn",
                label: p.label,
                description: p.description,
            }))
                // Stable sort: keep the backend order but push the catch-all purpose to the end.
                .sort((a, b) => (a.purpose === CATCH_ALL_PURPOSE ? 1 : 0) - (b.purpose === CATCH_ALL_PURPOSE ? 1 : 0));
            // Switch the UI to the recipient's resolved language. setLocale no-ops on an unsupported tag,
            // so the page keeps its current locale as a safety net.
            if (result.resolvedLocale) {
                setLocale(result.resolvedLocale);
            }
            appliedLocale = locale.value;
            state.value = "loaded";
        }
        catch (e) {
            handleFailure(e, "load");
        }
    }
    // The global LanguageSwitcher calls setLocale, which mutates `locale`. On a user switch, refetch so
    // the tenant catalogue labels come back in the chosen language (which now outranks the snapshot).
    // Skip our own programmatic application (next === appliedLocale) to avoid a redundant fetch on load.
    watch(locale, (next) => {
        if (next === appliedLocale) {
            return;
        }
        appliedLocale = next;
        void load(next);
    });
    /** Persist the per-purpose toggle states, then show the update-confirmation screen. */
    async function save() {
        state.value = "saving";
        try {
            await updateEmailPreferences(token, {
                changes: purposes.value.map((p) => ({ purpose: p.purpose, optedIn: p.checked })),
                globalOptOut: false,
            });
            await router.push({ name: "EmailPreferencesUpdated", params: { token } });
        }
        catch (e) {
            handleFailure(e, "save");
        }
    }
    /** Opt the recipient out of every marketing communication, then show the unsubscribed screen. */
    async function unsubscribeAll() {
        state.value = "unsubscribing";
        try {
            await updateEmailPreferences(token, {
                changes: purposes.value.map((p) => ({ purpose: p.purpose, optedIn: false })),
                globalOptOut: true,
            });
            await router.push({ name: "EmailPreferencesUnsubscribedAll", params: { token } });
        }
        catch (e) {
            handleFailure(e, "unsubscribe");
        }
    }
    return {
        state,
        data,
        purposes,
        isBusy,
        subscribedNewsletter,
        groups,
        purposeLabel,
        purposeDescription,
        toggleRow,
        load,
        save,
        unsubscribeAll,
    };
}
